/**
 * "Or continue with …" — the providers this deployment actually offers.
 *
 * Three decisions here are not visual.
 *
 * **Nothing is shown that will not work.** The list comes from `GET /methods`,
 * so a deployment with only Google configured shows one button. A hard-coded
 * three would show two that fail, and a user cannot tell that from an outage.
 *
 * **Apple only on Apple platforms.** Apple's guidelines make "Sign in with
 * Apple" the canonical option on their devices and it is out of place
 * elsewhere; the mobile client already does this, and doing it differently on
 * the web would be a difference nobody chose.
 *
 * **The navigation is a full page load, not a router push.** The destination is
 * Google's, not ours — `location.assign` is the only thing that gets there,
 * and using the router would silently do nothing.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiClient } from "@hardauto/core-api";
import { Alert, Button } from "@hardauto/core-ui";

import { NAMESPACE } from "../i18n";
import { useSession } from "../session/session";
import { useAuthMethods, type SocialProvider } from "./methods";
import "./social.css";

/** The label key and the mark for each provider. */
const PRESENTATION: Record<SocialProvider, { key: string; mark: string }> = {
  "social.google": { key: "social.google", mark: "G" },
  "social.microsoft": { key: "social.microsoft", mark: "⊞" },
  "social.apple": { key: "social.apple", mark: "" },
};

/** Whether this is an Apple platform, for Apple's own guideline. */
function onApplePlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent);
}

export interface SocialButtonsProps {
  client: ApiClient;
  /**
   * Where to return after signing in. Passed to the server, which checks it
   * against an allowlist — a target the callback could choose would be an
   * open redirect on an authentication endpoint.
   */
  redirectTo?: string;
}

export function SocialButtons({ client, redirectTo }: SocialButtonsProps) {
  const { t } = useTranslation(NAMESPACE);
  const session = useSession();
  const methods = useAuthMethods(client);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  if (!methods.socialEnabled || methods.providers.length === 0) return null;

  const visible = methods.providers.filter(
    (provider) => provider !== "social.apple" || onApplePlatform(),
  );
  if (visible.length === 0) return null;

  const begin = async (provider: SocialProvider) => {
    setBusy(provider);
    setFailed(null);
    try {
      const url = await session.startSocial(provider, redirectTo);
      // A full navigation. The destination is the provider's own host.
      window.location.assign(url);
    } catch {
      setFailed(provider);
      setBusy(null);
    }
  };

  return (
    <div className="hardauto-social">
      {/* A real separator, not a styled line: a screen reader announces it,
          which is what tells somebody the buttons below are alternatives to
          the form above rather than more of it. */}
      <div className="hardauto-social__divider" role="separator" aria-orientation="horizontal">
        <span>{t("social.divider")}</span>
      </div>

      {failed ? (
        <Alert tone="danger" title={t("social.failed")}>
          {t("social.failedBody")}
        </Alert>
      ) : null}

      <ul className="hardauto-social__list">
        {visible.map((provider) => (
          <li key={provider}>
            <Button
              variant="secondary"
              disabled={busy !== null}
              onClick={() => {
                void begin(provider);
              }}
            >
              <span className="hardauto-social__mark" aria-hidden="true">
                {PRESENTATION[provider].mark}
              </span>
              {/* The provider's name is in the visible label, so the
                  accessible name says which provider without an aria-label
                  repeating it. */}
              {t(`${PRESENTATION[provider].key}.continue`)}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
